import { marked } from "marked"
import { useCallback, useEffect, useRef, useState } from "react"
type CopiedValue = string | null

type CopyFn = (text: string) => Promise<boolean>

export function useCopyToClipboard(): [CopiedValue, CopyFn] {
  const [copiedText, setCopiedText] = useState<CopiedValue>(null)
  const timerId = useRef<NodeJS.Timeout | null>(null)

  const copy: CopyFn = useCallback(async (text) => {
    if (!navigator?.clipboard) {
      return false
    }

    // Try to save to clipboard then save it in the state if worked
    try {
      await navigator.clipboard.writeText(text)
      setCopiedText(text)
      timerId.current = setTimeout(() => setCopiedText(null), 3000)
      return true
    } catch {
      setCopiedText(null)
      return false
    }
  }, [])

  useEffect(() => {
    return () => {
      if (timerId.current) {
        clearTimeout(timerId.current)
      }
    }
  }, [])

  return [copiedText, copy]
}

export function useCopyToClipboardFormatted(): [CopiedValue, CopyFn] {
  const [copiedText, setCopiedText] = useState<CopiedValue>(null)
  const timerId = useRef<NodeJS.Timeout | null>(null)

  const copy: CopyFn = useCallback(async (markdownText: string) => {
    if (!navigator?.clipboard) {
      return false
    }

    try {
      const htmlText = marked(markdownText)
      if (typeof htmlText !== "string") {
        return false
      }

      const clipboardItem = new ClipboardItem({
        "text/plain": new Blob([markdownText], { type: "text/plain" }),
        "text/html": new Blob([htmlText], { type: "text/html" })
      })

      await navigator.clipboard.write([clipboardItem])

      setCopiedText(markdownText)
      timerId.current = setTimeout(() => setCopiedText(null), 1500)
      return true
    } catch {
      setCopiedText(null)
      return false
    }
  }, [])

  useEffect(() => {
    return () => {
      if (timerId.current) {
        clearTimeout(timerId.current)
      }
    }
  }, [])

  return [copiedText, copy]
}
